import { toUpdatePayload } from '../../profile/player'
import type { RosterCreatePayload } from '../../../services/team/rosterPlayer'
import type { RosterPlayerFormValues } from './types'

const trim = (v: string): string => (v ?? '').trim()

/**
 * Build the PUT body for editing an EXISTING roster player
 * (PUT /teams/:team/rosters/:roster/players/:playerId).
 *
 * Single-save model: the body carries ONLY what the user actually changed.
 * Personal fields are diffed `current` vs `initial` via the shared profile
 * `toUpdatePayload` (cleared fields sent as empty strings). Roster fields
 * (number, position, injured, affiliate, customer identifier, salesforce id)
 * are each diffed against `initial` and included only when changed. Image is
 * never in the body (handled by the separate immediate image step).
 */
export const toRosterUpdatePayload = (
  current: RosterPlayerFormValues,
  initial: RosterPlayerFormValues
): RosterCreatePayload => {
  // Personal: only changed fields (cleared → empty string), profile shape.
  const body = toUpdatePayload(current, initial) as RosterCreatePayload

  if (trim(current.playerNumber) !== trim(initial.playerNumber)) {
    body.player_number = trim(current.playerNumber)
  }
  if (trim(current.positionId) !== trim(initial.positionId)) {
    body.player_type_id = parseInt(trim(current.positionId), 10)
  }
  if (Boolean(current.isInjured) !== Boolean(initial.isInjured)) {
    body.is_injured = Boolean(current.isInjured)
  }
  if (Boolean(current.isAffiliate) !== Boolean(initial.isAffiliate)) {
    body.is_affiliate = Boolean(current.isAffiliate)
  }
  if (trim(current.customerIdentifier) !== trim(initial.customerIdentifier)) {
    body.customer_identifier = trim(current.customerIdentifier)
  }
  // Salesforce ID (Canlan only). The field is read-only on an existing player
  // (isRosterSalesforceIdLocked), so in practice this never fires on edit — it's
  // kept as a diff guard so a seeded value is never echoed back to the BE.
  if (trim(current.salesforceId) !== trim(initial.salesforceId)) {
    body.sf_id = trim(current.salesforceId)
  }

  return body
}
